import { useCallback, useLayoutEffect, useRef } from 'react';
import { useRadioAudioOwnership } from './useRadioParticipationOwner';

// A single generation owns queued work, the async claim, and audio.play().
// Cancellation releases that generation synchronously. Stale continuations never
// pause/release anything: a newer generation may already own the shared element.
export default function useRadioPlaybackAttempt({ audioRef, trackId, audioUrl, playerToken, enabled = true }) {
  const { claim, release } = useRadioAudioOwnership();
  const identity = JSON.stringify([trackId, audioUrl, playerToken, enabled]);
  const latest = useRef(identity);
  latest.current = identity;
  const generation = useRef(0);
  const timer = useRef(null);
  const started = useRef(false);
  const mounted = useRef(false);
  const mediaGeneration = useRef(null);

  const invalidate = useCallback(() => {
    generation.current++;
    mediaGeneration.current = null;
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    release();
    audioRef.current?.pause();
  }, [audioRef, release]);

  useLayoutEffect(() => {
    mounted.current = true;
    started.current = false;
    return () => { mounted.current = false; invalidate(); };
  }, [identity, invalidate]);

  const begin = useCallback(() => {
    const audio = audioRef.current;
    if (!mounted.current || latest.current !== identity || !enabled || !audio || !audioUrl ||
        audio.getAttribute('src') !== audioUrl) return null;
    invalidate();
    const version = generation.current;
    const valid = () => mounted.current && latest.current === identity && version === generation.current &&
      audioRef.current === audio && audio.getAttribute('src') === audioUrl;
    return {
      audio, valid,
      claim: async () => {
        const acquired = await claim();
        return valid() && acquired;
      },
      play: () => {
        if (!valid()) return undefined;
        mediaGeneration.current = version;
        return audio.play();
      },
      // currentSrc is affirmative when the browser has selected a resource.
      started: () => {
        if (!valid() || (audio.currentSrc && audio.currentSrc !== audio.src)) return false;
        started.current = true;
        return true;
      },
      cancel: () => { if (valid()) invalidate(); },
    };
  }, [audioRef, audioUrl, claim, enabled, identity, invalidate]);

  const schedule = useCallback(callback => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    const version = generation.current;
    const scheduledIdentity = latest.current;
    timer.current = window.setTimeout(() => {
      timer.current = null;
      if (mounted.current && generation.current === version && latest.current === scheduledIdentity) callback();
    }, 0);
  }, []);

  const explicitReason = useCallback(() =>
    !audioRef.current?.ended && (started.current || audioRef.current?.currentTime > 0) ? 'resume' : 'explicit', [audioRef]);

  // Native pause events can arrive after a newer play. Ignore those if the
  // element is currently playing; they cannot relinquish its new ownership.
  const onPause = useCallback(() => {
    if (mediaGeneration.current === generation.current && audioRef.current?.paused) invalidate();
  }, [audioRef, invalidate]);
  return { begin, invalidate, schedule, explicitReason, onPause };
}
